import type { Currency } from '../../constants';
import type { CartResponseResponse } from '../../model/cart/cart';

type Cart = CartResponseResponse['cart'];
export type CartAnalyticsLocation = 'cart' | 'cart_drawer' | 'unlock_reward_page';

export type CartAnalyticsTier = {
  id: string;
  minAmount: number;
  currentAmount: number;
  remaining: number;
  available: boolean;
};

export type CartAnalyticsContext = {
  cart_subtotal: number;
  currency: Currency;
  current_tier: number;
  next_tier: number | null;
  remaining_amount: number;
  item_count: number;
  ui_location: CartAnalyticsLocation;
};

/** Eligibility is server-owned; application of one coupon can supersede another. */
export function getCartAnalyticsTiers(cart: Cart): CartAnalyticsTier[] {
  const progress = cart.progressBar;
  const shipping = progress?.freeShipping;
  return [
    ...(shipping ? [{ ...shipping, id: 'shipping' }] : []),
    ...(progress?.discountTiers ?? []).map((tier) => ({ ...tier, id: `discount:${tier.couponCode}` })),
  ]
    .map(({ id, minAmount, currentAmount, remaining, available }) => ({
      id,
      minAmount,
      currentAmount,
      remaining,
      available,
    }))
    .sort((first, second) => first.minAmount - second.minAmount);
}

/** Common cart event properties in the cart's currency, without fixed campaign thresholds. */
export function getCartAnalyticsContext(cart: Cart, uiLocation: CartAnalyticsLocation): CartAnalyticsContext {
  const tiers = getCartAnalyticsTiers(cart);
  const next = tiers.find((tier) => !tier.available);
  // Use the same server subtotal that determines BMSM eligibility. When BMSM
  // is absent, use shipping progress, then the server's product subtotal.
  const subtotal =
    cart.progressBar?.discountTiers[0]?.currentAmount ??
    cart.progressBar?.freeShipping?.currentAmount ??
    cart.totals.totalPrice;
  return {
    cart_subtotal: subtotal,
    currency: cart.currency.currency,
    current_tier: Math.max(0, ...tiers.filter((tier) => tier.available).map((tier) => tier.minAmount)),
    next_tier: next?.minAmount ?? null,
    remaining_amount: next ? Math.max(0, next.remaining) : 0,
    item_count: cart.item.reduce((count, item) => count + item.quantity, 0),
    ui_location: uiLocation,
  };
}
